import { useMutation, useQuery } from '@apollo/client/react/hooks';
import { CKBox } from '@clearkit/react';
import CircularProgress from '@material-ui/core/CircularProgress';
import capitalize from 'lodash/capitalize';
import groupBy from 'lodash/groupBy';
import { useEffect, useMemo, useRef, useState } from 'react';

import LoadingArea from '~/components/LoadingArea';
import ToastsContainer from '~/containers/Toasts';
import useFeatureFlag from '~/hooks/account/useFeatureFlag';
import { readableType } from '~/lib/dataEnrichment';
import { usePollJobHistoryForStatus } from '~/lib/jobHistory/usePollJobHistoryForStatus';

import SelectiveEnrichmentConfig from '../SelectiveEnrichmentConfig';
import BackfillTriggerFields from './BackfillTriggerFields';
import BetaFieldMappingForm from './BetaFieldMappingForm';
import EmptyFieldMappingFields from './EmptyFieldMappingFields';
import EnrichmentToggle from './EnrichmentToggle';
import FieldMappingFooter from './FieldMappingFooter';
import FieldMappingFormFields from './FieldMappingFormFields';
import FieldMappingHeaders from './FieldMappingHeaders';
import RemoveMappingModal from './FieldMappingModals/RemoveMappingModal';
import SaveFieldMappingModal from './FieldMappingModals/SaveFieldMappingModal';
import fieldMappingQuery from './fieldMappingQuery.graphql';
import mutation from './mutation.graphql';
import query from './query.graphql';
import reexportProperties from './reexportProperties.graphql';
const STUB_MAPPING = {
  targetField: null,
  sourceField: null,
  sourceType: null,
  sourceObjectType: null,
  /* eslint-disable */
  // joaomdmoura: DISCLAIMER
  // We ended up changing the way this feature works after we implemented it,
  // and that led to some weird naming issue
  //
  // Despite the name being conditionaOverwrite, in the interface we indicate the
  // oposite value of this (if it would hard overwrite data or not)
  // hardOverwrite = !conditionalOverwrite
  //
  // By default the conditonalOverwrite should be set to true, as data is not
  // overwritten by default, and that will show the hardOverwrite Switch selector
  // as disabled
  /* eslint-enable */
  conditionalOverwrite: true, // hardOverwrite = false
  dataType: null,
  createdAt: null,
};

function clearbitTypesForMapping(type) {
  switch (type) {
    case 'account':
    case 'company':
      return ['clearbit_company'];
    default:
      return ['clearbit_company', 'clearbit_person'];
  }
}

function typeServices(type) {
  switch (type) {
    case 'salesforce':
      return ['salesforce', 'salesforcesandbox'];
    default:
      return [type];
  }
}

const FieldMappingForm = ({ integration, objectType }) => {
  const [fieldMappings, setFieldMappings] = useState([]);
  const [confirmSaveDialogOpen, setConfirmSaveDialogOpen] = useState(false);
  const [openDisableModal, setOpenDisableModal] = useState(false);
  const [showInvalidFields, setShowInvalidFields] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [triggering, setTriggering] = useState(false);
  const [reexportJobId, setReexportJobId] = useState(null);
  const [reexportJobQueuedAt, setReexportJobQueuedAt] = useState(null);

  const [
    refetchingPropertiesCompleted,
    setRefetchingPropertiesCompleted,
  ] = useState(false);
  // Using ref to get access to current state within the waitForPropertyRefresh callback
  const reexportRef = useRef();
  reexportRef.current = refetchingPropertiesCompleted;

  const hasEnrichmentBetaUIFlag = useFeatureFlag(
    'x_data_enrichment_new_ui_beta',
  ).enabled;
  const hasSelectiveEnrichmentFlag = useFeatureFlag(
    'x_selective_enrichment_beta',
  ).enabled;
  const {
    data,
    data: { accountConnector = {}, sourceAttributes, targetAttributes } = {},
    loading,
    refetch,
  } = useQuery(query, {
    fetchPolicy: 'no-cache',
    variables: {
      attributeType: capitalize(objectType.type),
      refreshAttributes: false,
      service: typeServices(integration),
      sourceTypes: [
        ...clearbitTypesForMapping(objectType.name),
        `custom_traits_${objectType.type}`,
        `reveal_${objectType.type}`,
        `segment_${objectType.type}`,
        `system_traits_${objectType.type}`,
      ],
      subCategory: objectType.name,
      targetObjectType: capitalize(objectType.name),
      targetTypes: [`${integration}_${objectType.name}`],
      externalObjectType: objectType.name,
    },
  });

  const [
    accountConnectorBackfillActive,
    setAccountConnectorBackfillActive,
  ] = useState(accountConnector?.backfillActive || false);

  const {
    data: { fieldMapping: savedFieldMapping } = {},
    loading: fieldMappingLoading,
  } = useQuery(fieldMappingQuery, {
    fetchPolicy: 'no-cache',
    variables: {
      accountConnectorId: accountConnector.id,
      targetObjectType: capitalize(objectType.name),
    },
    skip: !accountConnector.id,
  });

  const hasSavedSelectiveEnrichmentConfig = !!accountConnector
    ?.selectiveEnrichmentFilters?.filters;

  const isActive = !!savedFieldMapping?.enabled;

  const input = useMemo(
    () => ({
      id: accountConnector?.id,
      targetObjectType: capitalize(objectType.name),
      fieldMappings,
    }),
    [accountConnector, fieldMappings, objectType],
  );

  const [triggerReexportProperties] = useMutation(reexportProperties, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (rexportJobData) => {
      const reexportJobId = rexportJobData.reexportProperties.node.jobId;
      setReexportJobId(reexportJobId);
      setReexportJobQueuedAt(Date.now());
      ToastsContainer.addSuccess(
        'Refreshing Properties, this may take up to 30s to complete.',
      );
    },
    onError: (error) => {
      ToastsContainer.addError(
        `There was an error refreshing properties, please try again. ${error}`,
      );
    },
    variables: {
      input: { id: accountConnector.id },
    },
  });

  // onClickRefreshAttributes currently expects a callback that returns a promise so this is necessary in order
  // to get the refresh button state to properly reflect loading/complete state
  function waitForPropertyRefresh() {
    return new Promise((resolve) => {
      function checkPropertyRefreshStatus() {
        if (reexportRef.current) {
          resolve();
        } else {
          // Creating large set timeout to allow time for refetch of properties to complete before updating state
          window.setTimeout(checkPropertyRefreshStatus, 15000);
        }
      }
      checkPropertyRefreshStatus();
    });
  }
  const onClickRefreshAttributes = () => {
    if (typeServices(integration) == 'hubspot') {
      triggerReexportProperties();
      return waitForPropertyRefresh();
    } else {
      return refetch({ refreshAttributes: true });
    }
  };

  usePollJobHistoryForStatus({
    onCompleted: () => {
      refetch({ pollInterval: 500 });
      setReexportJobId(null);
      setReexportJobQueuedAt(null);
      setRefetchingPropertiesCompleted(true);
    },
    jobId: reexportJobId,
    minTimestamp: reexportJobQueuedAt,
    status: 'completed',
  });
  const [updateFieldMapping, { loading: isUpdating }] = useMutation(mutation, {
    onCompleted: () => {
      ToastsContainer.addSuccess(
        `${readableType(integration)} ${capitalize(
          objectType.name,
        )} field mapping saved!`,
      );
    },
    onError: () => {
      ToastsContainer.addError('There was an error saving this field mapping.');
    },
    variables: {
      input,
    },
  });

  useEffect(() => {
    (async () => {
      await refetch();
    })();
  }, [objectType]);

  const handleMappingChange = (index) => (changes) => {
    const newFieldMappings = [...fieldMappings];
    newFieldMappings[index] = {
      ...newFieldMappings[index],
      ...changes,
    };
    setFieldMappings(newFieldMappings);
  };

  const applyChangesToAllGroupMembers = (groupId) => (changes) => {
    const newFieldMappings = [...fieldMappings];
    const updatedMappings = newFieldMappings.reduce((result, mapping) => {
      if (mapping.groupId === groupId) {
        result.push({
          ...mapping,
          ...changes,
        });
      } else {
        result.push(mapping);
      }

      return result;
    }, []);
    setFieldMappings(updatedMappings);
  };

  const addMapping = (attributes = {}) => {
    attributes.createdAt = new Date().toISOString();

    setFieldMappings([...fieldMappings, { ...STUB_MAPPING, ...attributes }]);
  };

  const removeMapping = (indexToRemove) => () => {
    if (
      indexToRemove === 0 &&
      fieldMappings.length === 1 &&
      hasEnrichmentBetaUIFlag
    ) {
      setOpenDisableModal(true);
    } else {
      setFieldMappings(fieldMappings.filter((_, i) => i !== indexToRemove));
    }
  };

  const removeMappingGroup = (groupId) => () => {
    // returns true if all mappings have the same groupId, meaning that there
    // is only a single grouping left in this field mapping so we prompt a modal
    const lastMappingCheck = fieldMappings.every((mapping) => {
      return mapping.groupId === groupId;
    });

    if (!lastMappingCheck || !hasEnrichmentBetaUIFlag) {
      setFieldMappings(
        fieldMappings.filter((mapping) => mapping.groupId !== groupId),
      );
    } else {
      setOpenDisableModal(true);
    }
  };

  const updateErrors = (key, value) => {
    const errors = fieldErrors;
    if (value === undefined) {
      delete errors[key];
      setFieldErrors(errors);
    } else if (errors[key] === undefined) {
      errors[key] = [value];
      setFieldErrors(errors);
    } else {
      errors[key].push(value);
      setFieldErrors(errors);
    }
  };

  const findDuplicates = (array) => {
    return array.filter((value, index, arry) => {
      if (arry.indexOf(value) !== index) return value;
    }, []);
  };

  const areFieldMappingsValid = useMemo(() => {
    const nullFields = fieldMappings
      .map((fm) => [fm.targetField, fm.sourceField])
      .flat()
      .includes(null);

    const targetMappings = fieldMappings.map((fm) => fm.targetField);
    const targetFieldDuplicates = findDuplicates(targetMappings);
    if (targetFieldDuplicates.length > 0) {
      targetFieldDuplicates.forEach((targetField) => {
        updateErrors(targetField);
        updateErrors(targetField, 'duplicate field');
      });
    } else {
      setFieldErrors({});
    }

    return nullFields || targetFieldDuplicates.length > 0;
  }, [fieldMappings]);

  useEffect(() => {
    if (savedFieldMapping?.fieldMappings) {
      setFieldMappings([...savedFieldMapping?.fieldMappings]);
    } else {
      setFieldMappings([]);
    }
  }, [objectType.name, savedFieldMapping?.fieldMappings]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <CircularProgress />
      </div>
    );
  }

  const indexedMappings = fieldMappings.map((mapping, index) => {
    return { ...mapping, index };
  });

  const groupedMappings = groupBy(
    indexedMappings,
    (mapping) => mapping.groupId,
  );

  const objectTypeFormating = () => {
    if (objectType.name === 'company') {
      return 'companies';
    } else {
      return objectType.name + 's';
    }
  };

  const sortedMappings = Object.keys(groupedMappings)
    .reduce((result, key) => {
      if (key === 'undefined') {
        return result.concat(groupedMappings[key]);
      }

      result.push({
        groupId: key,
        conditionalOverwrite: groupedMappings[key][0].conditionalOverwrite,
        createdAt: groupedMappings[key][0].createdAt,
        fieldMappings: groupedMappings[key],
      });
      return result;
    }, [])
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  return (
    <LoadingArea
      loading={loading}
      className="flex justify-center w-full h-full">
      {hasEnrichmentBetaUIFlag ? (
        <div className="mx-4">
          <BetaFieldMappingForm
            integration={integration}
            isUpdating={isUpdating}
            areFieldMappingsValid={areFieldMappingsValid}
            addMapping={addMapping}
            setShowInvalidFields={setShowInvalidFields}
            setConfirmSaveDialogOpen={setConfirmSaveDialogOpen}
            objectType={objectType}
            fieldErrors={fieldErrors}
            fieldMappings={fieldMappings}
            sortedMappings={sortedMappings}
            handleMappingChange={handleMappingChange}
            applyChangesToAllGroupMembers={applyChangesToAllGroupMembers}
            removeMappingGroup={removeMappingGroup}
            removeMapping={removeMapping}
            sourceAttributes={sourceAttributes}
            targetAttributes={targetAttributes}
            showInvalidFields={showInvalidFields}
            onClickRefreshAttributes={onClickRefreshAttributes}
            hasEnrichmentBetaUIFlag={hasEnrichmentBetaUIFlag}
            confirmSaveDialogOpen={confirmSaveDialogOpen}
            triggering={triggering}
          />
        </div>
      ) : (
        <>
          <div
            key={`field-mapping-${objectType.name}`}
            className="flex flex-col justify-between h-full bg-gray-0">
            <div className="flex flex-col mt-8 mb-10">
              <div className="flex flex-row justify-between px-10">
                <div className="flex flex-col mr-14">
                  <p className="mb-2 text-base font-medium text-gray-800">
                    {capitalize(objectType.name)} enrichment settings
                  </p>
                  <p className="max-w-3xl text-sm text-gray-600">
                    Activate Clearbit Enrichment and determine if you'd like to
                    backfill historical records.
                  </p>
                </div>
                <CKBox className="p-4">
                  <EnrichmentToggle
                    accountConnector={accountConnector}
                    isActive={isActive}
                    loading={loading}
                    objectType={objectType}
                    integration={integration}
                  />

                  <LoadingArea loading={loading}>
                    <BackfillTriggerFields
                      integration={integration}
                      objectType={objectType}
                      accountConnector={accountConnector}
                      isActive={isActive}
                      setTriggering={setTriggering}
                      loading={loading}
                    />
                  </LoadingArea>
                </CKBox>
              </div>

              <div className="flex flex-row items-center pt-10 mx-10">
                <div className="flex flex-col">
                  <p className="mb-2 text-base font-medium text-gray-800">
                    {readableType(integration)} field mappings for enrichment
                  </p>
                  <p className="max-w-3xl text-sm text-gray-600">
                    Select the fields in {readableType(integration)}{' '}
                    {objectType.name}s that Clearbit will enrich, the source for
                    that data, and your data overwrite preference.{' '}
                    <a
                      href="https://help.clearbit.com/hc/en-us/articles/4420681386135"
                      className="text-blue-500 underline">
                      Learn more
                    </a>{' '}
                    about Clearbit data enrichment.
                  </p>
                </div>
              </div>
              <LoadingArea loading={fieldMappingLoading}>
                {fieldMappings.length === 0 ? (
                  <EmptyFieldMappingFields
                    integration={integration}
                    objectType={objectType}
                    addMapping={addMapping}
                    hasEnrichmentBetaUIFlag={hasEnrichmentBetaUIFlag}
                  />
                ) : (
                  <div className="px-10">
                    <FieldMappingHeaders />
                    <FieldMappingFormFields
                      fieldErrors={fieldErrors}
                      sortedMappings={sortedMappings}
                      handleMappingChange={handleMappingChange}
                      applyChangesToAllGroupMembers={
                        applyChangesToAllGroupMembers
                      }
                      removeMappingGroup={removeMappingGroup}
                      removeMapping={removeMapping}
                      addMapping={addMapping}
                      objectType={objectType}
                      sourceAttributes={sourceAttributes}
                      targetAttributes={targetAttributes}
                      integration={integration}
                      showInvalidFields={showInvalidFields}
                      onClickRefreshAttributes={onClickRefreshAttributes}
                    />
                  </div>
                )}
              </LoadingArea>
            </div>
            {!hasEnrichmentBetaUIFlag && (
              <FieldMappingFooter
                isUpdating={isUpdating}
                areFieldMappingsValid={areFieldMappingsValid}
                addMapping={addMapping}
                setShowInvalidFields={setShowInvalidFields}
                setConfirmSaveDialogOpen={setConfirmSaveDialogOpen}
                hasEnrichmentBetaUIFlag={hasEnrichmentBetaUIFlag}
              />
            )}
          </div>
        </>
      )}

      {hasSelectiveEnrichmentFlag &&
      hasEnrichmentBetaUIFlag &&
      integration === 'salesforce' ? (
        <div className="mt-10">
          <SelectiveEnrichmentConfig
            integration={integration}
            objectType={objectType}
          />
        </div>
      ) : null}

      {hasEnrichmentBetaUIFlag ? (
        <>
          <EnrichmentToggle
            hasEnrichmentBetaUIFlag={hasEnrichmentBetaUIFlag}
            accountConnector={accountConnector}
            isActive={isActive}
            loading={loading}
            objectType={objectType}
            integration={integration}
            hasSavedSelectiveEnrichmentConfig={
              hasSavedSelectiveEnrichmentConfig
            }
          />
          <LoadingArea loading={loading}>
            <BackfillTriggerFields
              integration={integration}
              objectType={objectType}
              accountConnector={accountConnector}
              isActive={isActive}
              setTriggering={setTriggering}
              loading={loading}
              hasSavedSelectiveEnrichmentConfig={
                hasSavedSelectiveEnrichmentConfig
              }
              hasEnrichmentBetaUIFlag={hasEnrichmentBetaUIFlag}
              accountConnectorBackfillActive={accountConnectorBackfillActive}
              setAccountConnectorBackfillActive={
                setAccountConnectorBackfillActive
              }
            />
          </LoadingArea>
        </>
      ) : null}

      <SaveFieldMappingModal
        confirmSaveDialogOpen={confirmSaveDialogOpen}
        setConfirmSaveDialogOpen={setConfirmSaveDialogOpen}
        objectTypeFormating={objectTypeFormating}
        readableType={readableType}
        integration={integration}
        triggering={triggering}
        isUpdating={isUpdating}
        updateFieldMapping={updateFieldMapping}
        input={input}
      />

      {hasEnrichmentBetaUIFlag ? (
        <RemoveMappingModal
          openDisableModal={openDisableModal}
          setOpenDisableModal={setOpenDisableModal}
          objectType={objectType}
          integration={integration}
          accountConnectorBackfillActive={accountConnectorBackfillActive}
          setFieldMappings={setFieldMappings}
        />
      ) : (
        <></>
      )}
    </LoadingArea>
  );
};

export default FieldMappingForm;
